/**
 * Touch controls, the pure half: what a finger POSITION means.
 *
 * The split is the same one src/ui/menu-model.ts makes against src/ui/menu.ts.
 * Everything here is arithmetic over numbers a caller supplies, so the mapping
 * from a thumb to a `CarInput` is tested in Node with no DOM, no pointer events
 * and no layout. ./touch.ts owns the elements and the pointer plumbing and
 * decides nothing.
 *
 * ===========================================================================
 * WHY THE STICK IS FLOATING, AND WHY THE CURVE IS RADIAL
 * ===========================================================================
 *
 * The stick has no fixed home. Wherever the left thumb lands becomes the
 * origin, and the vector to where it has since moved is the deflection. A
 * fixed-position stick asks the player to find a 90 px circle they cannot see
 * without looking away from the ball, and every miss is a car that does not
 * turn. `stickVector` therefore takes an origin-relative delta and knows
 * nothing about where on the screen either point was.
 *
 * The deadzone and saturation are applied to the MAGNITUDE of that delta, not
 * to each axis. Per-axis clamping is the classic mistake and it is visible in
 * play: it makes the reachable set a square, so a thumb pushed diagonally
 * reads 1.0 on both axes and the car pitches and yaws harder together than it
 * can either way alone. Radial keeps the reachable set a disc, which is what
 * both the gamepad path in ./controller.ts and the physics expect.
 *
 * SATURATION IS NOT COSMETIC. src/physics/aerial.ts behaves discretely
 * differently at exactly |input| = 1 — that is where air damping switches off
 * — so a control that can only asymptotically approach 1 makes every aerial
 * feel sluggish for a reason the player cannot see. The saturation radius is
 * comfortably inside the drawn ring so full deflection is a shove in the right
 * direction rather than a pixel-perfect placement.
 *
 * ===========================================================================
 * WHY THE THROTTLE IS NOT ON THE STICK
 * ===========================================================================
 *
 * The obvious layout — one stick, throttle on its Y axis — cannot express
 * "full throttle AND full steer", because a radial stick at 45 degrees is
 * 0.71 of each. That is the single most common input in the game. So the
 * stick is steering (and, airborne, pitch/yaw or pitch/roll exactly as the
 * keyboard's shared keys are), and the throttle is a button.
 *
 * Which leaves the right thumb holding accelerate while also needing jump and
 * boost, and it has one thumb. `autoThrottle` is the resolution and it is the
 * default: the car drives forward unless the brake is held. Players who want
 * the pedal back turn it off in Controls and get a Throttle button, and the
 * only difference in this file is the branch in `touchCarInput`.
 */

import type { CarInput } from '../physics/car-state';

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/**
 * The buttons, and the order they are built in. `throttle` is present in the
 * table whether or not `autoThrottle` hides it — the layout is a constant, and
 * a table that changed shape with a setting would make every position in
 * ./touch.ts depend on that setting too.
 */
export type TouchButtonId = 'throttle' | 'brake' | 'jump' | 'boost' | 'airRoll' | 'ballCam';

export const TOUCH_BUTTON_IDS: readonly TouchButtonId[] = Object.freeze([
  'throttle',
  'brake',
  'jump',
  'boost',
  'airRoll',
  'ballCam',
]);

export interface TouchButtonSpec {
  readonly id: TouchButtonId;
  /** Drawn on the button. Short enough to read at a glance mid-flight. */
  readonly label: string;
  /**
   * Shown under the label on the two buttons whose second job is not obvious:
   * the air-roll button is the powerslide on the ground, and the brake is
   * reverse once the car has stopped. Both are the SIMULATION's behaviour, not
   * this file's — see `touchCarInput`.
   */
  readonly sub?: string;
}

export const TOUCH_BUTTONS: readonly TouchButtonSpec[] = Object.freeze([
  { id: 'boost', label: 'Boost' },
  { id: 'jump', label: 'Jump' },
  { id: 'airRoll', label: 'Roll', sub: 'slide' },
  { id: 'brake', label: 'Brake', sub: 'reverse' },
  { id: 'throttle', label: 'Drive' },
  { id: 'ballCam', label: 'Ball Cam' },
]);

export type TouchButtonState = Readonly<Record<TouchButtonId, boolean>>;

export interface TouchStickState {
  /** True while a finger owns the stick. A released stick reads exactly zero. */
  readonly active: boolean;
  /** -1..1, positive to the RIGHT of the origin the finger set. */
  readonly x: number;
  /**
   * -1..1, positive DOWNWARDS.
   *
   * Screen coordinates, deliberately: it matches a gamepad's left-stick Y (see
   * the pad branch of ./controller.ts, where `pitch: ly`) so both devices hand
   * the same sign to the same field and neither needs a flip the other lacks.
   */
  readonly y: number;
}

export interface TouchState {
  readonly stick: TouchStickState;
  readonly buttons: TouchButtonState;
}

/**
 * A fresh all-up button map. MUTABLE on the way out — it is a new object every
 * call, and both the overlay and the tests fill one in before handing it on as
 * the readonly `TouchButtonState`.
 */
export const noTouchButtons = (): Record<TouchButtonId, boolean> => ({
  throttle: false,
  brake: false,
  jump: false,
  boost: false,
  airRoll: false,
  ballCam: false,
});

export const neutralTouchState = (): TouchState => ({
  stick: { active: false, x: 0, y: 0 },
  buttons: noTouchButtons(),
});

// ---------------------------------------------------------------------------
// The stick
// ---------------------------------------------------------------------------

/**
 * Deflection below this reads as zero, as a fraction of the stick radius.
 *
 * Much smaller than the gamepad's 0.12 in ./controller.ts, and for a reason
 * that is not a preference: a stick's deadzone exists to swallow HARDWARE
 * DRIFT, and a finger on glass has none. All this has to absorb is the couple
 * of pixels a thumb wanders while its owner believes it is still.
 */
export const TOUCH_DEADZONE = 0.07;

/**
 * At or beyond this fraction of the radius the stick reads exactly +/-1. Well
 * inside the ring — see the note on saturation in the header.
 */
export const TOUCH_SATURATION = 0.82;

/**
 * The live band, mapped onto 0..1. Continuous at both ends: exactly 0 at the
 * deadzone edge and exactly 1 at the saturation edge, so there is no step for
 * the player to feel at either.
 */
export const touchStickCurve = (raw: number): number => {
  if (!Number.isFinite(raw) || raw <= TOUCH_DEADZONE) return 0;
  if (raw >= TOUCH_SATURATION) return 1;
  return (raw - TOUCH_DEADZONE) / (TOUCH_SATURATION - TOUCH_DEADZONE);
};

/**
 * Deflection for a finger `dx, dy` pixels from where it first landed.
 *
 * `radius` is the distance that counts as full travel. Returns a vector on the
 * closed unit disc; direction is preserved exactly and only the magnitude is
 * shaped, which is what keeps a diagonal a diagonal.
 */
export const stickVector = (dx: number, dy: number, radius: number): { x: number; y: number } => {
  if (!(radius > 0)) return { x: 0, y: 0 };
  const distance = Math.hypot(dx, dy);
  if (!(distance > 0)) return { x: 0, y: 0 };
  const magnitude = touchStickCurve(distance / radius);
  if (magnitude === 0) return { x: 0, y: 0 };
  return { x: (dx / distance) * magnitude, y: (dy / distance) * magnitude };
};

// ---------------------------------------------------------------------------
// The mapping
// ---------------------------------------------------------------------------

/**
 * Touch state -> `CarInput`, for one tick.
 *
 * `airborne` selects the air mapping for the stick exactly as it does for the
 * keyboard and the pad: on the ground the X axis steers, in the air it yaws,
 * or rolls while the air-roll button is held. The sign conventions are the
 * simulation's, not the screen's — +steer, +yaw and +roll are all LEFT (see
 * the pad branch of ./controller.ts, which negates its stick for the same
 * reason this negates `x`).
 *
 * The air-roll button is also the handbrake, which is not a shortcut: the
 * keyboard binds both to one action and the pad reads L1 as both. A separate
 * powerslide button would be a sixth thing for one thumb to find and would do
 * nothing the player could not already do.
 */
export const touchCarInput = (
  state: TouchState,
  airborne: boolean,
  autoThrottle: boolean
): CarInput => {
  const { x, y } = state.stick;
  const b = state.buttons;
  const airRoll = b.airRoll;

  // With auto-throttle the car drives unless braked, which is also what makes
  // the brake a reverse: hold it past a standstill and the sim reverses,
  // exactly as it does for a held `KeyS`. Without it the two buttons are an
  // ordinary opposed pair and pressing both is a deliberate zero.
  const throttle = autoThrottle
    ? b.brake
      ? -1
      : 1
    : (b.throttle ? 1 : 0) - (b.brake ? 1 : 0);

  return {
    throttle,
    steer: -x,
    pitch: airborne ? y : 0,
    yaw: airborne && !airRoll ? -x : 0,
    roll: airborne && airRoll ? -x : 0,
    jump: b.jump,
    boost: b.boost,
    handbrake: airRoll,
  };
};

/**
 * True while the player is touching anything.
 *
 * Used for "press anything to skip the replay" (see `SkipLatch` in
 * src/render/replay.ts). `ballCam` is deliberately included: it is a touch,
 * and a player stabbing at the screen during a replay means "get on with it"
 * whichever button they hit.
 */
export const touchEngaged = (state: TouchState): boolean => {
  if (state.stick.active) return true;
  for (const id of TOUCH_BUTTON_IDS) if (state.buttons[id]) return true;
  return false;
};

// ---------------------------------------------------------------------------
// Whether to show them at all
// ---------------------------------------------------------------------------

/** The two facts the decision needs, so a test can state them directly. */
export interface PointerCapabilities {
  /** `navigator.maxTouchPoints`. */
  readonly maxTouchPoints: number;
  /** `matchMedia('(pointer: coarse)').matches`. */
  readonly coarsePointer: boolean;
}

export type TouchMode = 'auto' | 'on' | 'off';

/**
 * Whether the on-screen controls belong on this device.
 *
 * BOTH facts are required under `auto`, and the second one is the load-bearing
 * half. A Windows laptop with a touchscreen reports `maxTouchPoints > 0` while
 * its PRIMARY pointer is a mouse, so touch points alone would put a thumb
 * stick over the game of someone holding a keyboard — and, worse, hand them
 * auto-throttle, so their car would drive off on its own. `(pointer: coarse)`
 * asks about the primary pointer specifically, which is exactly the question:
 * is this a device whose main input is a finger.
 *
 * `on` and `off` are the escape hatches, and they are absolute — a player who
 * asked for the controls gets them on a desktop (the stick works with a mouse,
 * which is how the layout is checked without a phone), and a player who turned
 * them off does not get them on a tablet.
 */
export const touchControlsVisible = (mode: TouchMode, caps: PointerCapabilities): boolean => {
  if (mode === 'on') return true;
  if (mode === 'off') return false;
  return caps.maxTouchPoints > 0 && caps.coarsePointer;
};

/** Reads the live browser. Separated so `touchControlsVisible` stays pure. */
export const pointerCapabilities = (): PointerCapabilities => {
  const nav = typeof navigator === 'undefined' ? null : navigator;
  const coarse =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
  return { maxTouchPoints: nav?.maxTouchPoints ?? 0, coarsePointer: coarse };
};
